/**
 * Client binding for `generateAiPriceTrend` + hook.
 *
 * Lifecycle aligné sur `useAiSynthese` :
 *   - snapshot sur `/users/{uid}/fiches/{id}/ai/pricetrend` pour afficher
 *     instantanément un doc en cache,
 *   - callable qui force la fraîcheur via inputsHash côté serveur.
 *
 * Pas de regen manuelle côté UI : la narration suit automatiquement la
 * courbe (le bucketing de l'inputsHash ignore les micro-variations).
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot, type DocumentData } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";

export type AiPriceTrend = {
  version: 1;
  content: string;
  unavailableReason: string | null;
  generatedAt: string;
  model: string;
  inputsHash: string;
  pdfInclude: boolean;
};

const callGenerateAiPriceTrend = httpsCallable<
  { ficheId: string },
  AiPriceTrend
>(functions, "generateAiPriceTrend");

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: AiPriceTrend }
  | { status: "error"; message: string };

export function useAiPriceTrend(ficheId: string | null): State {
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (!ficheId) {
      setState({ status: "idle" });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    const ref = doc(
      db,
      "users",
      user.uid,
      "fiches",
      ficheId,
      "ai",
      "pricetrend",
    );
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        if (!snap.exists()) return;
        const parsed = parseAiPriceTrend(snap.data());
        if (parsed) setState({ status: "ready", data: parsed });
      },
      (err) => {
        console.warn("[ai/pricetrend] snapshot error", err);
      },
    );

    callGenerateAiPriceTrend({ ficheId })
      .then((res) => {
        if (cancelled) return;
        setState({ status: "ready", data: res.data as AiPriceTrend });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState((prev) =>
          prev.status === "ready"
            ? prev
            : { status: "error", message: extractMsg(e) },
        );
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [ficheId]);

  return state;
}

function parseAiPriceTrend(raw: DocumentData): AiPriceTrend | null {
  if (!raw || raw.version !== 1) return null;
  return {
    version: 1,
    content: typeof raw.content === "string" ? raw.content : "",
    unavailableReason:
      typeof raw.unavailableReason === "string" ? raw.unavailableReason : null,
    generatedAt: typeof raw.generatedAt === "string" ? raw.generatedAt : "",
    model: typeof raw.model === "string" ? raw.model : "",
    inputsHash: typeof raw.inputsHash === "string" ? raw.inputsHash : "",
    pdfInclude: Boolean(raw.pdfInclude),
  };
}

function extractMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "object" && e && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return String(e);
}
